import React, { useRef, useState } from "react";
import { Button, Input, Modal, Typography } from "antd";
import { TbUpload } from "react-icons/tb";
import { useTranslation } from "react-i18next";
import { useGlobalToast } from "../../context/ToastProvider";

function isPdfFile(file) {
  const fileType = String(file?.type || "").trim().toLowerCase();
  const fileName = String(file?.name || "").trim().toLowerCase();
  return fileType === "application/pdf" || fileName.endsWith(".pdf");
}

const UploadBookButton = ({ onUpload, isDisabled, isUploading = false }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useGlobalToast();

  const [selectedFile, setSelectedFile] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const inputRef = useRef(null);

  const handleOpen = () => {
    if (!isDisabled) setIsOpen(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && isPdfFile(file)) {
      setSelectedFile(file);
    } else {
      toast({
        title: t("uploadBook.errorTitle"),
        description: t("uploadBook.invalidFile"),
        status: "error",
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file && isPdfFile(file)) {
      setSelectedFile(file);
    } else {
      toast({
        title: t("uploadBook.errorTitle"),
        description: t("uploadBook.invalidFile"),
        status: "error",
      });
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile || !title.trim() || !description.trim()) {
      toast({
        title: t("uploadBook.errorTitle"),
        description: t("uploadBook.missingFields"),
        status: "error",
      });
      return;
    }

    let uploaded = false;
    setIsSubmitting(true);
    try {
      if (onUpload) {
        uploaded = (await onUpload({ file: selectedFile, title, description })) !== false;
      }
    } catch {
      uploaded = false;
    } finally {
      setIsSubmitting(false);
    }

    if (uploaded) {
      setSelectedFile(null);
      setTitle("");
      setDescription("");
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button
        icon={<TbUpload />}
        type="primary"
        onClick={handleOpen}
        disabled={isDisabled}
      >
        {t("uploadBook.button")}
      </Button>

      <Modal
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        onOk={handleSubmit}
        okText={t("uploadBook.submit")}
        cancelText={t("uploadBook.cancel")}
        title={t("uploadBook.modalTitle")}
        centered
        confirmLoading={isSubmitting || isUploading}
        okButtonProps={{ disabled: isSubmitting || isUploading }}
        cancelButtonProps={{ disabled: isSubmitting || isUploading }}
      >
        <div
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={handleDrop}
          onClick={() => inputRef.current && inputRef.current.click()}
          style={{
            border: "2px dashed #d0d5dd",
            borderRadius: 12,
            padding: 20,
            textAlign: "center",
            cursor: "pointer",
            background: selectedFile ? "#ecfdf3" : "#f9fafb",
            transition: "all 0.2s",
          }}
        >
          {selectedFile ? (
            <Typography.Text strong>{selectedFile.name}</Typography.Text>
          ) : (
            <Typography.Text type="secondary">{t("uploadBook.dragOrClick")}</Typography.Text>
          )}
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <Typography.Text>{t("uploadBook.titleLabel")}</Typography.Text>
          <Input
            placeholder={t("uploadBook.titlePlaceholder")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <Typography.Text>{t("uploadBook.descriptionLabel")}</Typography.Text>
          <Input.TextArea
            placeholder={t("uploadBook.descriptionPlaceholder")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
          />
        </div>
      </Modal>
    </>
  );
};

export default UploadBookButton;
